import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Lock, KeyRound } from "lucide-react";
import { Logo } from "@/components/Logo";
import { SiteFooter } from "@/components/Brand";
import { host, prod, errMsg } from "@/lib/prodApi";
import PrintLibrary from "@/components/production/PrintLibrary";
import VideoSlots from "@/components/production/VideoSlots";
import AudioLibrary from "@/components/production/AudioLibrary";
import CaseProgress from "@/components/production/CaseProgress";
import Auditions from "@/pages/Auditions";

const SECTIONS = [
  ["print", "Print library", PrintLibrary],
  ["videos", "Videos", VideoSlots],
  ["audio", "Audio", AudioLibrary],
  ["case", "Case progress", CaseProgress],
  ["auditions", "Auditions", Auditions],
];

function PinGate({ onOk }) {
  const [pin, setPin] = useState("");
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await host.post("/login", { pin });
      onOk();
    } catch (ex) {
      setErr(errMsg(ex, "Incorrect PIN"));
      setPin("");
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="max-w-xl py-16" data-testid="pin-gate">
      <h1 className="text-5xl font-black">Production room</h1>
      <p className="text-xl text-[#505a5f] mt-3">Crew only. Enter the host PIN to continue.</p>
      {err && (
        <div className="border-4 border-[#d4351c] p-5 mt-8" role="alert" data-testid="pin-error">
          <h2 className="text-2xl font-black">There is a problem</h2>
          <p className="error-msg text-xl mt-2">{err}</p>
        </div>
      )}
      <div className={`mt-8 ${err ? "form-error" : ""}`}>
        <label htmlFor="host-pin" className="form-label">Host PIN</label>
        <input id="host-pin" type="password" inputMode="numeric" autoComplete="off" value={pin} onChange={(e) => setPin(e.target.value)}
          className="form-input w-52 mt-2 tracking-[0.4em]" data-testid="pin-input" autoFocus />
      </div>
      <button type="submit" className="btn-spore mt-8" disabled={!pin || busy} data-testid="pin-submit"><KeyRound size={19} /> Unlock production room</button>
    </form>
  );
}

function Header({ authed, section, setSection, onLock }) {
  return (
    <header className="site-header-wrap">
      <div className="site-header">
        <div className="site-container h-[100px] flex items-center gap-8">
          <Logo height={44} />
          <span className="brand-sub" data-testid="production-label">Production room — crew only</span>
          {authed && <button className="btn-ghost ml-auto" onClick={onLock} data-testid="lock-button"><Lock size={18} /> Lock</button>}
        </div>
      </div>
      <div className="site-nav">
        <nav className="site-container flex items-center min-h-[62px]" aria-label="Production sections">
          {authed && SECTIONS.map(([key, label]) => (
            <button key={key} className={`nav-link ${section === key ? "active" : ""}`} onClick={() => setSection(key)} data-testid={`prod-nav-${key}`}>{label}</button>
          ))}
        </nav>
      </div>
    </header>
  );
}

export default function Production() {
  const [authed, setAuthed] = useState(null);
  const [params, setParams] = useSearchParams();
  const section = SECTIONS.some(([k]) => k === params.get("s")) ? params.get("s") : "print";

  useEffect(() => {
    host.get("/session").then((r) => setAuthed(r.data.authenticated)).catch(() => setAuthed(false));
    const id = prod.interceptors.response.use((r) => r, (e) => {
      if (e.response && e.response.status === 401) setAuthed(false);
      return Promise.reject(e);
    });
    return () => prod.interceptors.response.eject(id);
  }, []);

  const lock = async () => {
    await host.post("/logout");
    setAuthed(false);
  };
  const Section = (SECTIONS.find(([k]) => k === section) || SECTIONS[0])[2];
  return (
    <div className="relative z-10 min-h-screen flex flex-col bg-white" data-testid="production-room">
      <Header authed={authed} section={section} setSection={(s) => setParams({ s })} onLock={lock} />
      <main className="site-container w-full flex-1 py-12">
        {authed === null && <p className="text-lg">Checking host session…</p>}
        {authed === false && <PinGate onOk={() => setAuthed(true)} />}
        {authed && <div key={section} data-testid={`prod-section-${section}`}><Section /></div>}
      </main>
      <SiteFooter />
    </div>
  );
}
